import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Circle, Truck, Package, MapPin, AlertTriangle, Phone, XCircle } from 'lucide-react';
import api from '../api';

const WA_NUMBER  = '27686129215';
const TEL_NUMBER = '0686129215';

function WhatsAppIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// Linear progress steps shown on the tracking page
const STEPS = [
  { key: 'created',          label: 'Parcel Created',             icon: Package },
  // No city in the label — pickups are not always in Cape Town.
  { key: 'collected',        label: 'Collected',                  icon: Package },
  { key: 'at_depot',         label: 'Arrived at Gansbaai Depot',  icon: MapPin },
  { key: 'out_for_delivery', label: 'Out for Delivery',           icon: Truck },
  { key: 'delivered',        label: 'Delivered',                  icon: CheckCircle },
];

/**
 * Map ANY parcel status to the index in the STEPS array that best represents
 * where the parcel currently is.  Non-linear statuses map to the step they
 * came from, NOT the step they were heading towards.
 *
 * This means: if a parcel was accidentally scanned "delivered" and then
 * corrected back to "at_depot", the timeline correctly shows step 2 (At Depot)
 * as the current position — NOT step 4 (Delivered).
 */
const STATUS_TO_STEP = {
  created:           0,
  collected:         1,
  at_depot:          2,
  returned_to_depot: 2,  // came back to depot
  rescheduled:       2,  // rescheduled — sitting at depot
  out_for_delivery:  3,
  failed_delivery:   3,  // was out for delivery when it failed
  delivered:         4,
};

const FAILURE_LABELS = {
  client_not_home: 'Client not at home',
  out_of_time:     'Out of time',
  vehicle_breakdown:'Vehicle breakdown',
  wrong_address:   'Wrong address',
  refused:         'Refused delivery',
  other:           'Other',
};

// Friendly labels + icons for EVERY scan event, used in the full history log
const EVENT_META = {
  created:           { label: 'Parcel created',                icon: Package },
  collected:         { label: 'Collected',                     icon: Package },
  at_depot:          { label: 'Arrived at Gansbaai depot',     icon: MapPin },
  out_for_delivery:  { label: 'Out for delivery',              icon: Truck },
  delivered:         { label: 'Delivered',                     icon: CheckCircle },
  failed_delivery:   { label: 'Delivery attempt unsuccessful', icon: AlertTriangle },
  returned_to_depot: { label: 'Returned to depot',            icon: MapPin },
  rescheduled:       { label: 'Delivery rescheduled',          icon: Circle },
  cancelled:         { label: 'Cancelled',                     icon: XCircle },
};

export default function TrackParcel() {
  const { tracking } = useParams();

  const { data: parcel, isLoading, isError } = useQuery({
    queryKey: ['track', tracking],
    queryFn:  () => api.get(`/parcels/${tracking}`).then(r => r.data),
    refetchInterval: 30000, // re-check every 30 s
    staleTime: 0,           // always fetch fresh — corrections must show immediately
  });

  // ── Determine current position in the STEPS array ─────────────────────────
  const currentStepIdx = STATUS_TO_STEP[parcel?.status] ?? -1;
  const isDelivered    = parcel?.status === 'delivered';
  const isCancelled    = parcel?.status === 'cancelled';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-10 px-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-orange-500">Expressway Courier</h1>
          <p className="text-gray-500 text-sm">Parcel Tracking</p>
        </div>

        {isLoading && (
          <div className="text-center text-gray-400 py-20">Tracking your parcel…</div>
        )}
        {isError && (
          <div className="bg-white rounded-xl shadow p-8 text-center">
            <AlertTriangle size={40} className="mx-auto text-red-400 mb-3" />
            <p className="font-semibold text-gray-700">Parcel not found</p>
            <p className="text-gray-400 text-sm mt-1">Check your tracking number and try again.</p>
          </div>
        )}

        {parcel && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* ── Header ── */}
            <div className="bg-gray-900 text-white p-5">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Tracking Number</p>
              <p className="text-xl font-mono font-bold mt-1">{parcel.tracking_number}</p>
              <div className="mt-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">To</p>
                  <p className="text-sm font-semibold">{parcel.recipient_name}</p>
                  {parcel.recipient_address && (
                    <p className="text-xs text-gray-400">{parcel.recipient_address}</p>
                  )}
                </div>
                {parcel.tier_name && (
                  <span className="bg-orange-500 text-white text-xs px-3 py-1 rounded-full font-bold">
                    {parcel.tier_name}
                  </span>
                )}
              </div>
              {parcel.box_count > 1 && (
                <p className="mt-3 pt-3 border-t border-gray-700 text-xs text-gray-300">
                  📦 This is a <strong className="text-white">{parcel.box_count}-box</strong> shipment
                  {!isCancelled && parcel.boxes_at_status < parcel.box_count && (
                    <> — <strong className="text-amber-300">
                      {parcel.box_count - parcel.boxes_at_status} of {parcel.box_count}
                    </strong> have moved to the next stage. All boxes travel together and the
                    shipment only moves on once every box is scanned.</>
                  )}
                </p>
              )}
            </div>

            {/* ── Cancelled notice ── */}
            {isCancelled && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex items-center gap-2">
                  <XCircle size={18} className="text-red-600 flex-shrink-0" />
                  <p className="text-red-700 font-semibold text-sm">This parcel has been cancelled</p>
                </div>
                <p className="text-red-600 text-xs mt-1">
                  Please contact us if you have any questions.
                </p>
              </div>
            )}

            {/* ── Failed delivery notice ── */}
            {parcel.status === 'failed_delivery' && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <p className="text-red-700 font-semibold text-sm">Delivery attempt unsuccessful</p>
                {parcel.events
                  ?.filter(e => e.event_type === 'failed_delivery')
                  .slice(-1)
                  .map(e => (
                    <p key={e.id} className="text-red-600 text-xs mt-1">
                      Reason: {FAILURE_LABELS[e.failure_reason] || e.failure_reason}
                      {e.failure_notes ? ` — ${e.failure_notes}` : ''}
                    </p>
                  ))}
                <p className="text-red-600 text-xs mt-1">Our team will contact you to reschedule.</p>
              </div>
            )}

            {/* ── Returned to depot notice ── */}
            {parcel.status === 'returned_to_depot' && (
              <div className="bg-orange-50 border-l-4 border-orange-400 p-4">
                <p className="text-orange-700 font-semibold text-sm">Parcel returned to depot</p>
                <p className="text-orange-600 text-xs mt-1">
                  Your parcel could not be delivered and has been returned. Our team will contact you.
                </p>
              </div>
            )}

            {/* ── Timeline ── */}
            {!isCancelled && (
              <div className="p-5">
                <div className="space-y-4">
                  {STEPS.map((step, idx) => {
                    /**
                     * Position-based done/active logic:
                     * - done   = this step is STRICTLY BEFORE the current position
                     *            (or at/before if the parcel is fully delivered)
                     * - active = this step IS the current position (and not delivered,
                     *            which uses green done-styling for all steps)
                     *
                     * Crucially: if a parcel was scanned "delivered" then corrected
                     * back to "at_depot", currentStepIdx = 2 (at_depot).
                     * The "delivered" step (idx 4) evaluates as: done = 4 < 2 = FALSE
                     * and active = 4 === 2 = FALSE → it shows as gray. ✓
                     */
                    const done   = isDelivered ? idx <= currentStepIdx : idx < currentStepIdx;
                    const active = !isDelivered && currentStepIdx >= 0 && idx === currentStepIdx;

                    // Only show the timestamp when the step is actually reached
                    const event = parcel.events
                      ?.filter(e => e.event_type === step.key)
                      .slice(-1)[0];
                    const showTimestamp = (done || active) && event;

                    return (
                      <div key={step.key} className="flex items-start gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            done   ? 'bg-green-500 text-white' :
                            active ? 'bg-orange-500 text-white' :
                                     'bg-gray-100 text-gray-400'
                          }`}>
                            {done ? <CheckCircle size={16} /> : <Circle size={16} />}
                          </div>
                          {idx < STEPS.length - 1 && (
                            <div className={`w-0.5 h-8 mt-1 ${done ? 'bg-green-400' : 'bg-gray-200'}`} />
                          )}
                        </div>
                        <div className="pt-1">
                          <p className={`text-sm font-medium ${
                            done || active ? 'text-gray-800' : 'text-gray-400'
                          }`}>
                            {step.label}
                          </p>
                          {showTimestamp && (
                            <p className="text-xs text-gray-400">
                              {new Date(event.scanned_at).toLocaleString('en-ZA')}
                              {event.driver_name ? ` · ${event.driver_name}` : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Full history ── every scan event in order, newest first ── */}
            {parcel.events?.length > 0 && (
              <div className="border-t p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
                  Full History
                </p>
                <div className="space-y-3">
                  {[...parcel.events].reverse().map((e) => {
                    const meta   = EVENT_META[e.event_type] || { label: e.event_type, icon: Circle };
                    const Icon   = meta.icon;
                    const isFail = e.event_type === 'failed_delivery';
                    const isBack = e.event_type === 'returned_to_depot';
                    const tone   = isFail ? 'text-red-500'
                                 : isBack ? 'text-orange-500'
                                 : e.event_type === 'delivered' ? 'text-green-500'
                                 : 'text-gray-400';
                    return (
                      <div key={e.id} className="flex items-start gap-3">
                        <div className={`mt-0.5 flex-shrink-0 ${tone}`}>
                          <Icon size={16} />
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${
                            isFail ? 'text-red-600' : isBack ? 'text-orange-700' : 'text-gray-800'
                          }`}>
                            {meta.label}
                          </p>
                          {isFail && e.failure_reason && (
                            <p className="text-xs text-red-500">
                              Reason: {FAILURE_LABELS[e.failure_reason] || e.failure_reason}
                              {e.failure_notes ? ` — ${e.failure_notes}` : ''}
                            </p>
                          )}
                          <p className="text-xs text-gray-400">
                            {new Date(e.scanned_at).toLocaleString('en-ZA')}
                            {e.driver_name ? ` · ${e.driver_name}` : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Proof of Delivery ── */}
            {isDelivered && (() => {
              const BASE = 'https://expressway.xcourier.co.za';
              const proof = (parcel.events || [])
                .filter(e => e.event_type === 'delivered' && (e.photo_path || e.signature_path))
                .pop();
              if (!proof) return null;
              return (
                <div className="border-t p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-green-500 text-lg">✅</span>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Proof of Delivery</p>
                      <p className="text-xs text-gray-400">
                        {new Date(proof.scanned_at).toLocaleString('en-ZA')}
                        {proof.driver_name ? ` · ${proof.driver_name}` : ''}
                      </p>
                    </div>
                  </div>

                  {/* "Where is my parcel" is the question this page exists to
                      answer, and "left at the spot you asked for" is the answer
                      when nobody signed. */}
                  {proof.proof_type === 'left_at_spot' ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                      <p className="text-sm font-semibold text-amber-900">📦 Left at the agreed place</p>
                      {proof.proof_note && <p className="text-xs text-amber-800 mt-0.5">{proof.proof_note}</p>}
                    </div>
                  ) : proof.signed_by ? (
                    <p className="text-sm text-gray-700 mb-4">
                      Signed for by <strong>{proof.signed_by}</strong>
                    </p>
                  ) : null}

                  <div className="flex gap-3 flex-wrap mb-4">
                    {proof.photo_path && (
                      <div className="flex-1 min-w-[130px]">
                        <p className="text-xs text-gray-400 font-medium mb-1.5">📷 Delivery Photo</p>
                        <a href={`${BASE}${proof.photo_path}`} target="_blank" rel="noopener noreferrer">
                          <img
                            src={`${BASE}${proof.photo_path}`}
                            alt="Delivery photo"
                            className="w-full max-h-56 object-cover rounded-xl border border-gray-100 shadow-sm hover:opacity-90 transition-opacity cursor-zoom-in"
                          />
                        </a>
                      </div>
                    )}
                    {proof.signature_path && (
                      <div className="flex-1 min-w-[130px]">
                        <p className="text-xs text-gray-400 font-medium mb-1.5">✍️ Recipient Signature</p>
                        <a href={`${BASE}${proof.signature_path}`} target="_blank" rel="noopener noreferrer">
                          <div className="w-full rounded-xl border border-gray-100 shadow-sm bg-gray-50 flex items-center justify-center p-3 hover:opacity-90 transition-opacity cursor-zoom-in" style={{ minHeight: 110 }}>
                            <img
                              src={`${BASE}${proof.signature_path}`}
                              alt="Recipient signature"
                              className="max-h-32 object-contain"
                            />
                          </div>
                        </a>
                      </div>
                    )}
                  </div>

                  {proof.latitude && proof.longitude && (
                    <a
                      href={`https://www.google.com/maps?q=${proof.latitude},${proof.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors"
                    >
                      <MapPin size={13} />
                      View delivery location on Google Maps
                    </a>
                  )}
                </div>
              );
            })()}

            {/* ── Contact footer ── */}
            <div className="border-t bg-gray-50 p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 text-center">
                Need help? Contact us
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href={`tel:${TEL_NUMBER}`}
                  className="flex items-center gap-2 bg-white border border-gray-200 hover:border-orange-400 text-gray-700 hover:text-orange-500 font-medium text-sm px-5 py-2.5 rounded-lg transition-colors w-full sm:w-auto justify-center">
                  <Phone size={16} className="text-orange-400" />
                  068 612 9215
                </a>
                <a href={`https://wa.me/${WA_NUMBER}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[#25D366] hover:bg-[#20b558] text-white font-medium text-sm px-5 py-2.5 rounded-lg transition-colors w-full sm:w-auto justify-center">
                  <WhatsAppIcon size={16} />
                  WhatsApp Us
                </a>
              </div>
              <div className="flex items-center justify-center gap-1.5 mt-4">
                <MapPin size={13} className="text-gray-400" />
                <p className="text-xs text-gray-400">56 Barnard Street, Gansbaai, 7220</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
